export function is_credit_order(order) {
    return String(order?.payment_type || order?.payment_method || "").trim().toLowerCase().includes("credit");
}

export function get_order_status(order) {
    return String(order?.order_status_name || order?.order_status || "").trim().toLowerCase();
}

export function is_credit_dashboard_order(order) {
    if (!is_credit_order(order)) return false;

    const status = get_order_status(order);

    return ![
        "pending",
        "approved",
        "accepted",
        "rejected",
        "declined",
        "cancelled",
    ].includes(status);
}

export function format_status_label(value) {
    return String(value || "")
        .trim()
        .split(/[\s_-]+/)
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");
}

export function is_order_paid(order) {
    return ["delivered", "paid"].includes(get_order_status(order));
}

export function get_order_payment_label(order) {
    return is_credit_order(order) ? "Credit" : "Pay Now";
}

export function get_order_delivery_label(order) {
    return order?.pargo_delivery ? "Delivery" : "Collection";
}

export function get_order_month_key(order) {
    const raw_date = order?.created_at;
    if (!raw_date) return "";

    const date = new Date(raw_date);
    if (Number.isNaN(date.getTime())) return "";

    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, "0");
    return `${year}-${month}`;
}

export function format_month_label(month_key) {
    const [year, month] = String(month_key || "").split("-");
    if (!year || !month) return "All Months";

    const date = new Date(Number(year), Number(month) - 1, 1);
    return date.toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
    });
}

export function format_price(price) {
    return Number(price || 0).toLocaleString("en-ZA", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
}

export function format_short_date(dateString) {
    if (!dateString) return "";

    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "";

    return date.toLocaleDateString("en-US", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
}

export function get_order_products_label(order) {
    const item_names = (order?.items || [])
        .map((item) => item?.product_name || item?.product?.name)
        .filter(Boolean);

    const product_names = item_names.length
        ? item_names
        : (order?.products || [])
            .map((item) => item?.product?.name || item?.name)
            .filter(Boolean);

    if (!product_names.length) return "Order Items";
    if (product_names.length === 1) return product_names[0];

    return `${product_names[0]} + ${product_names.length - 1} more`;
}

export function get_customer_initials(name) {
    return String(name || "")
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join("") || "CU";
}

export function build_month_options(orders = []) {
    const seen = new Set();
    const options = [];

    orders.forEach((order) => {
        const month_key = get_order_month_key(order);
        if (!month_key || seen.has(month_key)) return;
        seen.add(month_key);
        options.push({
            value: month_key,
            label: format_month_label(month_key),
        });
    });

    return options.sort((a, b) => b.value.localeCompare(a.value));
}

export function credit_month_matches(order, selected_month) {
    return selected_month === "all" || get_order_month_key(order) === selected_month;
}

export function credit_query_matches(order, query) {
    if (!query) return true;

    const lowered_query = String(query).trim().toLowerCase();

    return String(order?.buyer_name || "").toLowerCase().includes(lowered_query)
        || String(order?.order_number || "").toLowerCase().includes(lowered_query)
        || get_order_products_label(order).toLowerCase().includes(lowered_query);
}

export function credit_status_matches(order, status_filter) {
    if (status_filter === "all") return true;
    if (status_filter === "settled") return is_order_paid(order);
    if (status_filter === "outstanding") return !is_order_paid(order);
    return false;
}

export function credit_order_matches(order, query, selected_month, status_filter) {
    return credit_month_matches(order, selected_month)
        && credit_query_matches(order, query)
        && credit_status_matches(order, status_filter);
}

export function sales_query_matches(order, query) {
    if (!query) return true;

    const lowered_query = String(query).trim().toLowerCase();

    return String(order?.buyer_name || "").toLowerCase().includes(lowered_query)
        || String(order?.order_number || "").toLowerCase().includes(lowered_query);
}

export function sales_payment_matches(order, payment_filter) {
    if (payment_filter === "all") return true;
    if (payment_filter === "credit") return is_credit_order(order);
    if (payment_filter === "paid") return is_order_paid(order);
    return false;
}

export function sales_delivery_matches(order, delivery_filter) {
    if (delivery_filter === "all") return true;
    if (delivery_filter === "collection") return !order?.pargo_delivery;
    if (delivery_filter === "delivery") return !!order?.pargo_delivery;
    return false;
}

export function sales_order_matches(order, query, payment_filter, delivery_filter) {
    return sales_query_matches(order, query)
        && sales_payment_matches(order, payment_filter)
        && sales_delivery_matches(order, delivery_filter);
}

export function build_customer_accounts(orders = []) {
    const grouped = new Map();

    orders.forEach((order) => {
        const customer_id = String(order?.buyer_id || order?.user_id || order?.buyer_name || order?.id);
        if (!grouped.has(customer_id)) {
            grouped.set(customer_id, {
                id: customer_id,
                buyer_name: order?.buyer_name || "Unknown Customer",
                orders: [],
                order_count: 0,
                unpaid_count: 0,
                paid_count: 0,
                outstanding_amount: 0,
                paid_amount: 0,
            });
        }

        const customer_account = grouped.get(customer_id);
        const amount = Number(order?.total_amount || 0);

        customer_account.orders.push(order);
        customer_account.order_count += 1;

        if (is_order_paid(order)) {
            customer_account.paid_amount += amount;
            customer_account.paid_count += 1;
            return;
        }

        customer_account.outstanding_amount += amount;
        customer_account.unpaid_count += 1;
    });

    return Array.from(grouped.values()).sort((a, b) => b.outstanding_amount - a.outstanding_amount);
}

export function build_sales_stats(filtered_sales_orders = [], month_filtered_orders = []) {
    const paid_orders = filtered_sales_orders.filter((order) => is_order_paid(order));
    const unresolved_orders = filtered_sales_orders.filter((order) => !is_order_paid(order));
    const collection_orders = filtered_sales_orders.filter((order) => !order?.pargo_delivery);
    const delivery_orders = filtered_sales_orders.filter((order) => !!order?.pargo_delivery);
    const cancelled_orders = month_filtered_orders.filter((order) =>
        ["rejected", "cancelled"].includes(get_order_status(order))
    );
    const total_sales_amount = filtered_sales_orders.reduce((sum, order) => sum + Number(order?.total_amount || 0), 0);

    return ({
        total_sales_amount,
        paid_sales_amount: paid_orders.reduce((sum, order) => sum + Number(order?.total_amount || 0), 0),
        unresolved_sales_amount: unresolved_orders.reduce((sum, order) => sum + Number(order?.total_amount || 0), 0),
        unpaid_orders_count: unresolved_orders.length,
        total_orders: filtered_sales_orders.length,
        collection_orders: collection_orders.length,
        delivery_orders: delivery_orders.length,
        cancelled_orders: cancelled_orders.length,
    });
}

export function build_credit_stats(filtered_credit_orders = [], customer_accounts = []) {
    const outstanding_orders = filtered_credit_orders.filter((order) => !is_order_paid(order));
    const paid_orders = filtered_credit_orders.filter((order) => is_order_paid(order));

    return ({
        customers_on_credit: customer_accounts.length,
        outstanding_amount: outstanding_orders.reduce((sum, order) => sum + Number(order?.total_amount || 0), 0),
        paid_amount: paid_orders.reduce((sum, order) => sum + Number(order?.total_amount || 0), 0),
        total_credit_orders: filtered_credit_orders.length,
        outstanding_count: outstanding_orders.length,
        paid_count: paid_orders.length,
    });
}
